import { PosterTextSet } from './qr-poster-texts.model';

export const QR_POSTER_TEXT_FIT_VALUES = ['wrap', 'shrink', 'singleLineShrink'] as const;
export type QrPosterTextFit = typeof QR_POSTER_TEXT_FIT_VALUES[number];

export function normalizeQrPosterTextFit(value: unknown, fallback: QrPosterTextFit = 'wrap'): QrPosterTextFit {
  return typeof value === 'string' && (QR_POSTER_TEXT_FIT_VALUES as readonly string[]).includes(value)
    ? value as QrPosterTextFit
    : fallback;
}

export type QrPosterBlockType =
  | 'text'
  | 'variableText'
  | 'image'
  | 'icon'
  | 'companyLogo'
  | 'qrCode'
  | 'line'
  | 'rectangle'
  | 'iconText'
  | 'iconMultilineText'
  | 'emergencyNumbers'
  | 'footer';

export interface QrPosterTemplate {
  version: 1;
  selectedTemplateId: string;
  source: 'scanafy-classic' | 'system-template' | 'custom';
  page: {
    format: 'a5';
    orientation: 'portrait';
    widthMm: number;
    heightMm: number;
  };
  branding: {
    showScanafyFooter: boolean;
  };
  blocks: QrPosterBlock[];
  updatedAt?: string;
}

export interface QrPosterBlock {
  id: string;
  type: QrPosterBlockType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  locked?: boolean;
  hidden?: boolean;
  zIndex?: number;
  content?: {
    text?: string;
    de?: string;
    en?: string;
    variable?: string;
    icon?: string;
    imageAssetKey?: string;
    imageUrl?: string;
    fit?: 'contain' | 'cover' | 'stretch';
    rows?: Array<{ label: string; value: string; description?: string }>;
  };
  style?: {
    fontSize?: number;
    fontWeight?: 'normal' | 'bold';
    textAlign?: 'left' | 'center' | 'right';
    textFit?: QrPosterTextFit;
    color?: string;
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    opacity?: number;
    iconColor?: string;
    gap?: number;
    centerIconTextComposite?: boolean;
  };
}

export interface QrPosterRenderContext {
  companyName: string;
  companyPhone: string;
  companyWebsite: string;
  companyAddress: string;
  companyDisplayEmail: string;
  companyLogoBase64: string;
  propertyAddress: string;
  qrTargetUrl: string;
  qrDataUrl: string;
  themeColor: string;
  appMode: string;
  posterTexts: PosterTextSet;
  icons: Record<string, string | undefined>;
}

export interface QrPosterSystemTemplateVariant {
  id: string;
  name: string;
  colors: {
    primary?: string;
    headerBg?: string;
    headerText?: string;
    accent?: string;
    accentLight?: string;
    border?: string;
    borderMuted?: string;
    sectionBg?: string;
    ctaBg?: string;
    footerText?: string;
    tableBg?: string;
    tableBorder?: string;
    iconColor?: string;
  };
}

export interface QrPosterSystemTemplateOption {
  id: string;
  name: string;
  description?: string;
  qrPosterTemplate: QrPosterTemplate;
  variants?: QrPosterSystemTemplateVariant[];
}

export interface SavedQrPosterTemplate {
  id: string;
  name: string;
  template: QrPosterTemplate;
  createdAt?: string;
  updatedAt?: string;
}

export interface QrPosterTemplateState {
  qrPosterTemplate: QrPosterTemplate | null;
  activeTemplate: QrPosterTemplate | null;
  activeTemplateId: string | null;
  templates: SavedQrPosterTemplate[];
  savedTemplate?: SavedQrPosterTemplate | null;
  clearedLandingProfileCount?: number;
}

export const DEFAULT_QR_POSTER_PAGE = {
  format: 'a5' as const,
  orientation: 'portrait' as const,
  widthMm: 148,
  heightMm: 210,
};
